<template>
  <div class="vs-page">
    <div v-if="loading" class="vs-loading">Loading…</div>
    <div v-if="error" class="vs-error">{{ error }}</div>

    <div v-if="!loading && visit" class="vs-doc">
      <header class="vs-header">
        <div>
          <h1 class="vs-fac-name">{{ facility.hospital_name || 'Hospital' }}</h1>
          <div class="vs-fac-sub">
            <span v-if="facility.hospital_phone">Tel: {{ facility.hospital_phone }}</span>
            <span v-if="facility.hospital_email"> · {{ facility.hospital_email }}</span>
          </div>
        </div>
        <div class="vs-doc-id">
          <div><strong>VISIT SUMMARY</strong></div>
          <div>Visit #: {{ visit.visit_number }}</div>
          <div class="vs-muted">Issued: {{ formatDt(new Date()) }}</div>
        </div>
      </header>

      <!-- Patient block -->
      <section class="vs-section">
        <div class="vs-grid">
          <div><strong>Patient:</strong> {{ patient.name }}</div>
          <div><strong>Patient #:</strong> {{ patient.patient_no }}</div>
          <div><strong>Sex:</strong> {{ patient.gender }}</div>
          <div><strong>DOB:</strong> {{ formatDate(patient.dob) }} ({{ ageYears(patient.dob) }} yrs)</div>
          <div><strong>Visit date:</strong> {{ formatDt(visit.visit_date) }}</div>
          <div><strong>Department:</strong> {{ visit.department || '—' }}</div>
          <div><strong>Doctor:</strong> {{ visit.doctor_name ? 'Dr. ' + visit.doctor_name : '—' }}<span v-if="visit.doctor_specialty"> ({{ visit.doctor_specialty }})</span></div>
          <div><strong>Disposition:</strong> {{ formatDisposition(visit.disposition) }}</div>
          <div v-if="visit.chief_complaint" class="vs-grid-full">
            <strong>Reason for visit:</strong> {{ visit.chief_complaint }}
          </div>
        </div>
      </section>

      <!-- Vitals -->
      <section class="vs-section" v-if="vitals">
        <h2 class="vs-h2">Vital signs</h2>
        <div class="vs-vitals-grid">
          <div v-if="vitals.bp_systolic"><strong>{{ vitals.bp_systolic }}/{{ vitals.bp_diastolic }}</strong><div class="vs-muted">mmHg · BP</div></div>
          <div v-if="vitals.pulse"><strong>{{ vitals.pulse }}</strong><div class="vs-muted">bpm · Pulse</div></div>
          <div v-if="vitals.respiratory_rate"><strong>{{ vitals.respiratory_rate }}</strong><div class="vs-muted">rpm · Resp</div></div>
          <div v-if="vitals.temperature"><strong>{{ vitals.temperature }}{{ vitals.temp_unit || 'C' }}°</strong><div class="vs-muted">Temp</div></div>
          <div v-if="vitals.oxygen_saturation"><strong>{{ vitals.oxygen_saturation }}%</strong><div class="vs-muted">SpO2</div></div>
          <div v-if="vitals.weight"><strong>{{ vitals.weight }} kg</strong><div class="vs-muted">Weight</div></div>
          <div v-if="vitals.height"><strong>{{ vitals.height }} cm</strong><div class="vs-muted">Height</div></div>
          <div v-if="vitals.blood_sugar"><strong>{{ vitals.blood_sugar }}</strong><div class="vs-muted">mmol/L · BGL</div></div>
        </div>
      </section>

      <!-- Notes (SOAP) -->
      <section class="vs-section" v-if="notes.length">
        <h2 class="vs-h2">Consultation notes</h2>
        <div v-for="(n, i) in notes" :key="i" class="vs-note">
          <div class="vs-muted-sm">{{ n.author ? 'Dr. ' + n.author : '' }} · {{ formatDt(n.created_at) }}</div>
          <div v-if="n.subjective"><strong>Subjective:</strong> {{ n.subjective }}</div>
          <div v-if="n.objective"><strong>Objective:</strong> {{ n.objective }}</div>
          <div v-if="n.assessment"><strong>Assessment:</strong> {{ n.assessment }}</div>
          <div v-if="n.plan"><strong>Plan:</strong> {{ n.plan }}</div>
        </div>
      </section>

      <!-- Diagnoses -->
      <section class="vs-section" v-if="diagnoses.length">
        <h2 class="vs-h2">Diagnoses</h2>
        <ul class="vs-list">
          <li v-for="(d, i) in diagnoses" :key="i">
            <span class="vs-pill" :class="d.diagnosis_type === 'primary' ? 'vs-pill-primary' : 'vs-pill-secondary'">{{ d.diagnosis_type }}</span>
            <strong>{{ d.diagnosis_name }}</strong>
            <span v-if="d.icd_code" class="vs-muted-sm"> · ICD {{ d.icd_code }}</span>
            <div v-if="d.notes" class="vs-muted-sm">{{ d.notes }}</div>
          </li>
        </ul>
      </section>

      <!-- Prescriptions -->
      <section class="vs-section" v-if="prescriptions.length">
        <h2 class="vs-h2">Prescriptions issued</h2>
        <div v-for="(p, i) in prescriptions" :key="i" class="vs-rx">
          <div><strong>{{ p.prescription_number }}</strong> <span class="vs-muted-sm">— {{ formatDate(p.prescription_date) }}</span></div>
          <pre class="vs-pre">{{ p.items }}</pre>
          <div v-if="p.notes" class="vs-muted-sm">Notes: {{ p.notes }}</div>
        </div>
      </section>

      <!-- Lab orders -->
      <section class="vs-section" v-if="labs.length">
        <h2 class="vs-h2">Laboratory ordered</h2>
        <ul class="vs-list">
          <li v-for="(l, i) in labs" :key="i">
            <strong>{{ l.request_number }}</strong>: {{ l.tests }}
            <span class="vs-muted-sm"> · {{ formatDate(l.request_date) }}</span>
          </li>
        </ul>
      </section>

      <!-- Education provided -->
      <section class="vs-section" v-if="education.length">
        <h2 class="vs-h2">Education provided</h2>
        <ul class="vs-list">
          <li v-for="(e, i) in education" :key="i">
            <strong>{{ e.topic }}</strong> ({{ e.material_format }}, {{ e.language }})
            <div v-if="e.notes" class="vs-muted-sm">{{ e.notes }}</div>
          </li>
        </ul>
      </section>

      <!-- Disposition / next steps -->
      <section class="vs-section" v-if="visit.disposition_notes">
        <h2 class="vs-h2">Next steps</h2>
        <p>{{ visit.disposition_notes }}</p>
      </section>

      <footer class="vs-footer">
        <div class="vs-disclaimer">
          This is a copy of your visit summary. Keep it for your records and bring it to follow-up appointments. If your symptoms worsen, contact us via the patient portal or call our emergency line.
        </div>
        <div class="vs-actions no-print">
          <button class="vs-btn" onclick="window.print()">🖨 Print</button>
          <button class="vs-btn vs-btn-secondary" @click="$router.back()">← Back</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

export default {
  name: "PatientVisitPrint",
  data() {
    return { loading: true, error: '', visit: null, vitals: null, notes: [], diagnoses: [], prescriptions: [], labs: [], education: [], patient: {}, facility: {} };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      const token = localStorage.getItem("patientPortalToken");
      if (!token) { this.$router.push('/portal/login'); return; }
      try {
        const r = await axios.post(hms,
          { function: 'patientPortalVisitSummary', visit_id: this.$route.params.id },
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
        if (r.data && r.data.success) {
          const d = r.data.data;
          this.visit = d.Visit; this.vitals = d.Vitals; this.notes = d.Notes || [];
          this.diagnoses = d.Diagnoses || []; this.prescriptions = d.Prescriptions || [];
          this.labs = d.Labs || []; this.education = d.Education || [];
          this.patient = d.Patient || {}; this.facility = d.Facility || {};
          this.loading = false;
          if (this.$route.query.auto === '1') {
            this.$nextTick(() => setTimeout(() => window.print(), 400));
          }
        } else {
          this.error = r.data?.message || 'Failed to load summary.';
          this.loading = false;
        }
      } catch (e) {
        this.error = 'Network error or session expired.';
        this.loading = false;
      }
    },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDt(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    ageYears(dob) { if (!dob) return '?'; const d = new Date(dob); if (isNaN(d)) return '?'; return Math.floor((Date.now() - d.getTime()) / 31557600000); },
    formatDisposition(d) { return ({ admitted:'Admitted', discharged:'Discharged home', referred_out:'Referred out', theatre:'Sent to theatre', morgue:'Mortuary', lwbs:'Left without being seen', dama:'Discharged against medical advice', observation:'Observation', transferred:'Transferred' })[d] || (d || '—'); }
  }
};
</script>

<style scoped>
.vs-page { background: #f1f5f9; min-height: 100vh; padding: 24px; font-family: 'Helvetica Neue', sans-serif; }
.vs-loading, .vs-error { max-width: 600px; margin: 60px auto; text-align: center; color: #475569; }
.vs-error { color: #dc2626; }

.vs-doc { max-width: 850px; margin: 0 auto; background: #fff; padding: 36px 44px; box-shadow: 0 4px 14px rgba(0,0,0,.08); border-radius: 4px; color: #1e293b; }

.vs-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 18px; border-bottom: 3px solid #1a4480; margin-bottom: 24px; }
.vs-fac-name { font-size: 24px; font-weight: 800; color: #1a4480; margin: 0; }
.vs-fac-sub { color: #64748b; font-size: 12px; margin-top: 4px; }
.vs-doc-id { text-align: right; font-size: 13px; }
.vs-doc-id strong { font-size: 14px; color: #1a4480; letter-spacing: 1px; }
.vs-muted { color: #94a3b8; }
.vs-muted-sm { color: #94a3b8; font-size: 11px; }

.vs-section { margin-bottom: 22px; }
.vs-h2 { font-size: 13px; font-weight: 700; color: #1a4480; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }

.vs-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 24px; font-size: 13px; }
.vs-grid-full { grid-column: 1 / -1; }

.vs-vitals-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.vs-vitals-grid > div { background: #f8fafc; padding: 10px 12px; border-radius: 6px; text-align: center; font-size: 16px; }
.vs-vitals-grid strong { font-size: 18px; color: #1a4480; }

.vs-note { background: #f8fafc; padding: 12px 14px; border-radius: 6px; margin-bottom: 8px; font-size: 13px; line-height: 1.6; }
.vs-note > div { margin-bottom: 4px; }

.vs-list { margin: 0; padding: 0 0 0 20px; font-size: 13px; }
.vs-list li { margin-bottom: 8px; }

.vs-pill { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; margin-right: 6px; }
.vs-pill-primary { background: #1a4480; color: #fff; }
.vs-pill-secondary { background: #e2e8f0; color: #475569; }

.vs-rx { background: #fefce8; border: 1px solid #fde68a; padding: 10px 14px; border-radius: 6px; margin-bottom: 8px; font-size: 13px; }
.vs-pre { white-space: pre-wrap; font-family: inherit; margin: 6px 0; padding: 8px; background: #fff; border-radius: 4px; font-size: 13px; line-height: 1.5; }

.vs-footer { margin-top: 36px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
.vs-disclaimer { font-size: 10px; color: #94a3b8; font-style: italic; margin-bottom: 12px; }
.vs-actions { display: flex; gap: 8px; }
.vs-btn { padding: 8px 16px; background: #1a4480; color: #fff; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 13px; }
.vs-btn-secondary { background: #fff; color: #1a4480; border: 1px solid #1a4480; }

@media print {
  .vs-page { background: #fff; padding: 0; }
  .vs-doc { box-shadow: none; max-width: none; padding: 16mm; }
  .no-print { display: none !important; }
}
</style>
